/**
 * ============================================================================
 * TimeBasedPromotionFormModal - Create/Edit Time-Based Promotions
 * ============================================================================
 *
 * Production-ready modal for creating and editing time-based promotions with:
 * - Sport-specific filtering
 * - Day of week selection
 * - Time range selection
 * - Form validation
 * - TanStack Query mutations
 *
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Percent, Tag as TagIcon, AlertCircle } from 'lucide-react';
import { useCreatePromotion, useUpdatePromotion } from '../../hooks/usePromotions';
import type { Promotion, CreatePromotionData } from '../../types/promotions';
import Modal from '../ui/Modal';

interface TimeBasedPromotionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  venueId: number;
  sports: Array<{ id: number; name: string }>;
  editingPromotion?: Promotion | null;
}

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

export function TimeBasedPromotionFormModal({
  isOpen,
  onClose,
  venueId,
  sports,
  editingPromotion,
}: TimeBasedPromotionFormModalProps) {
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount_percentage: '',
    valid_from: '',
    valid_until: '',
    sport_id: '' as number | '',
    days_of_week: [] as string[],
    start_time: '',
    end_time: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Mutations
  const createMutation = useCreatePromotion();
  const updateMutation = useUpdatePromotion();

  // Initialize form when editing
  useEffect(() => {
    if (editingPromotion) {
      setFormData({
        title: editingPromotion.title || '',
        description: editingPromotion.description || '',
        discount_percentage: editingPromotion.discount_percentage.toString(),
        valid_from: editingPromotion.valid_from.split('T')[0], // Extract date only
        valid_until: editingPromotion.valid_until.split('T')[0],
        sport_id: editingPromotion.sport_id || '',
        days_of_week: editingPromotion.days_of_week || [],
        start_time: editingPromotion.start_time || '',
        end_time: editingPromotion.end_time || '',
      });
    } else {
      // Reset form for new promotion
      setFormData({
        title: '',
        description: '',
        discount_percentage: '',
        valid_from: '',
        valid_until: '',
        sport_id: '',
        days_of_week: [],
        start_time: '',
        end_time: '',
      });
    }
    setErrors({});
    setTouched({});
  }, [editingPromotion, isOpen]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 50) {
      newErrors.title = 'Title must be 50 characters or less';
    }

    if (formData.description && formData.description.length > 150) {
      newErrors.description = 'Description must be 150 characters or less';
    }

    if (!formData.discount_percentage) {
      newErrors.discount_percentage = 'Discount percentage is required';
    } else {
      const discount = parseFloat(formData.discount_percentage);
      if (isNaN(discount) || discount <= 0 || discount > 100) {
        newErrors.discount_percentage = 'Discount must be between 0 and 100';
      }
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Start date is required';
    }

    if (!formData.valid_until) {
      newErrors.valid_until = 'End date is required';
    }

    // Date range validation
    if (formData.valid_from && formData.valid_until) {
      const startDate = new Date(formData.valid_from);
      const endDate = new Date(formData.valid_until);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today && !editingPromotion) {
        newErrors.valid_from = 'Start date cannot be in the past';
      }

      if (endDate < startDate) {
        newErrors.valid_until = 'End date must be after start date';
      }
    }

    // Time range validation
    if (formData.start_time && !formData.end_time) {
      newErrors.end_time = 'End time is required when start time is set';
    }
    if (formData.end_time && !formData.start_time) {
      newErrors.start_time = 'Start time is required when end time is set';
    }
    if (formData.start_time && formData.end_time) {
      if (formData.start_time >= formData.end_time) {
        newErrors.end_time = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur
  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Handle day of week toggle
  const handleDayToggle = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter((d) => d !== day)
        : [...prev.days_of_week, day],
    }));
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      title: true,
      discount_percentage: true,
      valid_from: true,
      valid_until: true,
    });

    if (!validateForm()) {
      return;
    }

    // Prepare data for API
    const promotionData: CreatePromotionData = {
      title: formData.title.trim(),
      description: formData.description.trim() || undefined,
      discount_percentage: parseFloat(formData.discount_percentage),
      valid_from: formData.valid_from,
      valid_until: formData.valid_until,
      sport_id: formData.sport_id || undefined,
      days_of_week: formData.days_of_week.length > 0 ? formData.days_of_week : undefined,
      start_time: formData.start_time || undefined,
      end_time: formData.end_time || undefined,
    };

    try {
      if (editingPromotion) {
        await updateMutation.mutateAsync({
          venueId,
          promotionId: editingPromotion.id,
          data: promotionData,
        });
      } else {
        await createMutation.mutateAsync({
          venueId,
          data: promotionData,
        });
      }
      onClose();
    } catch (error) {
      console.error('Failed to save promotion:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to save promotion',
      });
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" preventClose={isSubmitting} animate={false}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {editingPromotion ? 'Edit Promotion' : 'Create Time-Based Promotion'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Schedule discounts for specific days and times
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-2">
                ({formData.title.length}/50)
              </span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              onBlur={() => handleBlur('title')}
              maxLength={50}
              placeholder="e.g., Weekend Morning Special"
              className={`w-full px-4 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                touched.title && errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              disabled={isSubmitting}
            />
            {touched.title && errors.title && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description (Optional)
              <span className="text-gray-400 font-normal ml-2">
                ({formData.description.length}/150)
              </span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              maxLength={150}
              rows={3}
              placeholder="Brief description of the promotion"
              className={`w-full px-4 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              disabled={isSubmitting}
            />
            {errors.description && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.description}
              </p>
            )}
          </div>

          {/* Discount & Sport Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Discount Percentage */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Discount Percentage <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.discount_percentage}
                  onChange={(e) =>
                    setFormData({ ...formData, discount_percentage: e.target.value })
                  }
                  onBlur={() => handleBlur('discount_percentage')}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="e.g., 20"
                  className={`w-full pl-4 pr-10 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    touched.discount_percentage && errors.discount_percentage
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                <Percent className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
              {touched.discount_percentage && errors.discount_percentage && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.discount_percentage}
                </p>
              )}
            </div>

            {/* Sport Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Apply to Sport (Optional)
              </label>
              <div className="relative">
                <select
                  value={formData.sport_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sport_id: e.target.value ? parseInt(e.target.value) : '',
                    })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  disabled={isSubmitting}
                >
                  <option value="">All Sports</option>
                  {sports.map((sport) => (
                    <option key={sport.id} value={sport.id}>
                      {sport.name}
                    </option>
                  ))}
                </select>
                <TagIcon className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Valid From <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  onBlur={() => handleBlur('valid_from')}
                  className={`w-full pl-4 pr-10 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    touched.valid_from && errors.valid_from
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                <Calendar className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              {touched.valid_from && errors.valid_from && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.valid_from}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Valid Until <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  onBlur={() => handleBlur('valid_until')}
                  className={`w-full pl-4 pr-10 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    touched.valid_until && errors.valid_until
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                <Calendar className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              {touched.valid_until && errors.valid_until && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.valid_until}
                </p>
              )}
            </div>
          </div>

          {/* Days of Week */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Active Days (Optional)
            </label>
            <div className="grid grid-cols-7 gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => handleDayToggle(day.value)}
                  className={`py-2 px-3 rounded-lg text-sm font-semibold transition-colors ${
                    formData.days_of_week.includes(day.value)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  disabled={isSubmitting}
                >
                  {day.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Leave empty to apply to all days of the week
            </p>
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Start Time (Optional)
              </label>
              <div className="relative">
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className={`w-full pl-4 pr-10 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.start_time ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                <Clock className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              {errors.start_time && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.start_time}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                End Time (Optional)
              </label>
              <div className="relative">
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className={`w-full pl-4 pr-10 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.end_time ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                <Clock className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              {errors.end_time && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.end_time}
                </p>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Leave time range empty to apply promotion all day
          </p>

          {/* Error message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? editingPromotion
                  ? 'Updating...'
                  : 'Creating...'
                : editingPromotion
                ? 'Update Promotion'
                : 'Create Promotion'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
